import { ChevronLeft, ChevronRight, MessageSquare, Plus, Search, X } from "lucide-react";
import { ConnectionLogo } from "./ConnectionLogo";
import { choiceRouteWord, filterChoices, groupChoices, pickerChoices, routeWord } from "./connectionsModel";

// The Add panel: a search over everything the workspace can connect, grouped the way the catalog groups it,
// and one row per app saying how it connects. Once an app is chosen the page hands the panel the step to show.

/** One row of the picker. */
export function ChoiceRow({ choice, disabled = false, onPick, testId }) {
  const route = choiceRouteWord(choice);
  return (
    <button type="button" className="cx-choice" data-testid={testId || `agent-connections-pick-${choice.app}`} disabled={disabled} onClick={() => onPick(choice)}>
      <ConnectionLogo app={choice.app} label={choice.label} size={34} />
      <span className="cx-choice__copy">
        <span className="cx-choice__name">{choice.label}</span>
        <span className={`cx-choice__route${choice.flow === "chat" ? " cx-choice__route--chat" : ""}`}>{route}</span>
      </span>
      {choice.flow === "chat" ? <MessageSquare size={15} aria-hidden="true" /> : <ChevronRight size={15} aria-hidden="true" />}
    </button>
  );
}

/** The picker: search, grouped rows, Shopify sign-in when the workspace offers it, and Something else. */
export function ChoicePicker({ choices, query, onQuery, onPick, onPickCustom, shopify = null, busyKey = null, agentName = "your agent" }) {
  const filtered = filterChoices(pickerChoices(choices), query);
  const groups = groupChoices(filtered);
  const showShopify = shopify?.offered && (!query || "shopify sign in".includes(query.toLowerCase().trim()));
  return (
    <>
      <label className="cx-search">
        <Search size={16} aria-hidden="true" />
        <span className="sr-only">Search apps</span>
        <input type="search" value={query} onChange={(event) => onQuery(event.target.value)} placeholder={`Search apps ${agentName} can connect`} data-testid="connection-catalog-search" autoComplete="off" autoFocus />
      </label>
      <div className="cx-choices">
        {showShopify ? (
          <div className="cx-choices__group">
            <p className="cx-choices__title">Commerce</p>
            <div className="cx-choices__grid">
              <ChoiceRow choice={{ app: "shopify", label: "Shopify", flow: "shopify_oauth" }} testId="agent-connections-pick-shopify" disabled={busyKey === "shopify"} onPick={shopify.onPick} />
            </div>
          </div>
        ) : null}
        {groups.map((group) => (
          <div key={group.title} className="cx-choices__group">
            <p className="cx-choices__title">{group.title}</p>
            <div className="cx-choices__grid">
              {group.items.map((choice) => <ChoiceRow key={choice.app} choice={choice} disabled={busyKey === `add:${choice.app}` || busyKey === choice.app} onPick={onPick} />)}
            </div>
          </div>
        ))}
        {!groups.length && !showShopify ? <p className="cx-choices__empty">Nothing matches “{query}”. Try another word, or add it as a key below.</p> : null}
        <div className="cx-choices__group">
          <p className="cx-choices__title">Anything else</p>
          <div className="cx-choices__grid">
            <button type="button" className="cx-choice" data-testid="agent-connections-pick-custom" onClick={onPickCustom}>
              <span className="cx-logo" style={{ width: 34, height: 34, borderRadius: 9, color: "#003B8D", background: "rgba(70,144,217,0.12)", boxShadow: "none" }} aria-hidden="true"><Plus size={16} /></span>
              <span className="cx-choice__copy"><span className="cx-choice__name">Something else</span><span className="cx-choice__route">{routeWord("custom")}</span></span>
              <ChevronRight size={15} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * The panel's frame. With `step.kind === "pick"` it shows the picker; otherwise it shows `children` (the step the
 * page is on) under a back button. `headingRef`/`panelRef` let the page focus and reveal it.
 */
export function ConnectPanel({ step, children, onClose, onBack, headingRef, panelRef, title = "Add a connection", stepTitle = null, stepApp = null, ...picker }) {
  const picking = step?.kind === "pick";
  return (
    <section ref={panelRef} className="cx-panel" data-testid="agent-connections-add-panel" aria-labelledby="connection-setup-heading">
      <header className="cx-panel__head">
        {picking ? (
          <h2 ref={headingRef} id="connection-setup-heading" className="cx-panel__title" tabIndex={-1}>{title}</h2>
        ) : (
          <div className="cx-flow__head">
            <button type="button" className="cx-btn cx-btn--sm cx-btn--ghost cx-flow__back" onClick={onBack} aria-label="Back to all apps"><ChevronLeft size={15} />All apps</button>
            {stepApp ? <ConnectionLogo app={stepApp.app} label={stepApp.label} size={32} /> : null}
            <h2 ref={headingRef} id="connection-setup-heading" className="cx-flow__title" tabIndex={-1}>{stepTitle || title}</h2>
          </div>
        )}
        <button type="button" className="cx-btn cx-btn--sm cx-btn--ghost cx-btn--icon" onClick={onClose} aria-label="Close"><X size={15} /></button>
      </header>
      {picking ? <ChoicePicker {...picker} /> : <div className="cx-flow">{children}</div>}
    </section>
  );
}

export default ConnectPanel;
